import concat from 'lodash/concat';
import filter from 'lodash/filter';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import size from 'lodash/size';
import get from 'lodash/get';
import first from 'lodash/first';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';
import moment from 'moment';
import {
  getHouseKeys,
  getJobTimeWindows,
  getSequenceNumbersOfJobs,
  getStopLocation,
  isRecharge,
  isBreak,
  isDepartureOrArrival as isDepartureOrArrivalType,
  getMaxTimeOnVehicle,
  isParkingStop,
  getVehicleSOC,
} from './SolutionHelpers';
import {
  getOrdersByStop,
  getLabelsFromOrders,
  getOrdersByLocation,
  getOrdersByInternalID,
  getAddressFromOrders,
  getOrdersAddress,
  hasHighPriorityOrders,
  getOrdersByActivity,
  getJobPosition,
  compareLocationLatAndLng,
} from './OrdersHelpers';
import { getBoundingCoordinates } from './MapHelpers';
import { getSafeValue } from './security';
import { filterAreasByType, isOrderInTerritory } from './territories/TerritoriesHelpers';
import { tourColors } from '../global/variables';
import { AREA_TYPES } from './territories/TerritoriesFromJson';
import { ORDER_ACTIVITIES } from './csv/config';

const getPudoGroupIds = (activities, groupsAndPudos) => {
  if (!groupsAndPudos || !activities) return [];
  const pudoJobIds = activities.filter((a) => a.type === 'pudo').map((a) => a.jobId);
  if (pudoJobIds.length === 0) return [];
  return uniq(
    groupsAndPudos
      .filter((g) => (g.pudos || []).some((p) => pudoJobIds.includes(p.id)))
      .map((g) => g.id),
  );
};

const getPudoPositions = (activities, groupsAndPudos) => {
  if (!groupsAndPudos || !activities) return [];
  const pudoJobIds = activities.filter((a) => a.type === 'pudo').map((a) => a.jobId);
  if (pudoJobIds.length === 0) return [];
  return uniq(
    compact(
      groupsAndPudos
        .flatMap((g) => g.pudos || [])
        .filter((p) => pudoJobIds.includes(p.id))
        .map((p) => p.assignAt),
    ),
  );
};

const mapFeatures = (coordinates = []) =>
  coordinates.map(({ coordinate, properties = {} }) => ({
    type: 'Feature',
    geometry: { coordinates: coordinate, type: 'Point' },
    properties,
  }));

export const calculateBBox = (coordinates, distance = 100) => {
  if (coordinates.length === 1) return getBoundingCoordinates(coordinates[0], distance);
  return [
    Math.min(...coordinates.map((stop) => stop[0])),
    Math.min(...coordinates.map((stop) => stop[1])),
    Math.max(...coordinates.map((stop) => stop[0])),
    Math.max(...coordinates.map((stop) => stop[1])),
  ];
};

const getFeaturePerPlace = (job, place, jobNumbers, orders, coordinates, unassigned, types) => {
  const stopOrders = getOrdersByInternalID(getOrdersByLocation(orders, place.location), job.id);
  const address = !isEmpty(stopOrders) ? stopOrders[0].Address : null;
  const labels = getLabelsFromOrders(stopOrders);
  return {
    type: 'Feature',
    geometry: { coordinates, type: 'Point' },
    properties: {
      jobId: job.id,
      jobNumber: getSafeValue(jobNumbers, job.id),
      reason: unassigned.reasons,
      labels,
      address,
      types,
    },
  };
};

export const convertToGeoJSON = (
  solution,
  stopsNoLocation,
  plannedJobs = [],
  orders = [],
  tourPlanner,
  areas,
  depot,
  groupsAndPudos,
) => {
  if (!solution) {
    return undefined;
  }

  const geoJSON = {};
  const features = [];
  const allCoordinates = [];
  const jobNumbers = getSequenceNumbersOfJobs(plannedJobs);
  const tw = getJobTimeWindows(plannedJobs);
  const hk = getHouseKeys(plannedJobs);
  const maxTime = getMaxTimeOnVehicle(plannedJobs);

  const visitedLocations = {};
  const batteryCapacity =
    tourPlanner?.vehicleProfiles?.[0]?.evProfile?.specs?.batteryCapacity ?? null;

  (solution.tours || []).forEach((tour, tourIndex) => {
    if (!tour.stops) return;
    const coordinates = [];
    const jobMarkerColor = tourColors[tourIndex % tourColors.length];
    // Initial charge in kWh: use this vehicle's initialCharges% if set, otherwise maxCharge%.
    const initialChargePercent = getVehicleSOC(tourPlanner, tour.vehicleId);
    const initialChargeKwh =
      batteryCapacity != null && initialChargePercent != null
        ? (batteryCapacity * initialChargePercent) / 100
        : null;
    const maxChargePercent = tourPlanner?.vehicleProfiles?.[0]?.evProfile?.charging?.maxCharge;
    const maxChargeKwh =
      batteryCapacity != null && maxChargePercent != null
        ? (batteryCapacity * maxChargePercent) / 100
        : null;
    // Consumption is cumulative for the whole tour, but the battery is topped up to maxCharge
    // at every recharge stop. Track the charge baseline (kWh) and how much cumulative
    // consumption to subtract back out, both reset at each recharge, so charge is computed
    // relative to the most recent recharge (or tour start if none yet).
    let chargeBaselineKwh = initialChargeKwh;
    let consumptionOffset = 0;
    tour.stops.forEach((stop, index) => {
      const stopLocation = getStopLocation(stop, stopsNoLocation, index, tourIndex);
      coordinates.push([stopLocation.lng, stopLocation.lat]);
      const stopOrders = getOrdersByStop(orders, stop);
      let address = getOrdersAddress(stopOrders);
      const labels = getLabelsFromOrders(stopOrders);
      const positionFromOrders = getJobPosition(stopOrders);
      const position =
        positionFromOrders.length > 0
          ? positionFromOrders
          : getPudoPositions(stop.activities, groupsAndPudos);
      const highPriority = hasHighPriorityOrders(stopOrders);
      if (index === 0) {
        address =
          tourPlanner &&
          tourPlanner.location &&
          tourPlanner.location.value &&
          tourPlanner.location.value.lat &&
          tourPlanner.location.label;
      }
      if (
        index === size(tour.stops) - 1 &&
        tourPlanner &&
        tourPlanner.returnLocation !== undefined &&
        tourPlanner.returnLocation.value !== null
      ) {
        address = tourPlanner.returnLocation.label;
      }

      const territories = filterAreasByType(areas, AREA_TYPES.TERRITORY);
      const groups = filterAreasByType(areas, AREA_TYPES.GROUP);

      const isParking = isParkingStop(stop);

      const firstActivity = stop.activities.find(
        (a) => !isBreak(a.type) && !isDepartureOrArrivalType(a.type),
      );

      let parkingDuration = null;
      if (isParking && firstActivity?.time?.start && stop.time?.arrival) {
        const stopArrival = moment(stop.time.arrival);
        const firstActivityStart = moment(firstActivity.time.start);
        if (stopArrival.isValid() && firstActivityStart.isValid()) {
          parkingDuration = firstActivityStart.diff(stopArrival, 'minutes');
        }
      }

      const isRechargeStop = stop.activities.some((a) => isRecharge(a.type));
      // Charge relative to the most recent recharge (or tour start if none yet), since the
      // battery is topped back up to maxCharge every time the vehicle recharges.
      const chargeKwh =
        isRechargeStop && maxChargeKwh != null
          ? maxChargeKwh
          : chargeBaselineKwh != null && stop.consumption != null
          ? Math.max(0, chargeBaselineKwh - (stop.consumption - consumptionOffset))
          : null;
      if (isRechargeStop && stop.consumption != null && maxChargeKwh != null) {
        consumptionOffset = stop.consumption;
        chargeBaselineKwh = maxChargeKwh;
      }

      features.push({
        type: 'Feature',
        geometry: { coordinates: [stopLocation.lng, stopLocation.lat], type: 'Point' },
        properties: {
          // filtering out breaks
          jobNumbers: stop.activities
            .map((a) => getSafeValue(jobNumbers, a.jobId))
            .filter((v) => v !== undefined),
          tw: stop.activities.map((a) => getSafeValue(tw, a.jobId)).filter((v) => v !== undefined),
          routeId: tourIndex,
          arr_time: stop.time.arrival,
          dep_time: stop.time.departure,
          jobOrder: index,
          skills: uniq(compact(stopOrders.map((order) => order.Skills))),
          types: isParking ? ['parking'] : stop.activities.map((a) => a.type),
          jobIds: stop.activities.map((a) => a.jobId),
          jobTags: stop.activities.map((a) => a.jobTag).filter((val) => val),
          vehicle: { type: tour.vehicleId },
          territory: isOrderInTerritory(stopOrders, territories),
          group: isOrderInTerritory(stopOrders, groups),
          pudoGroupId: getPudoGroupIds(stop.activities, groupsAndPudos),
          labels,
          address,
          highPriority,
          jobMarkerColor,
          position,
          load: stop.load,
          categories: uniq(compact(stopOrders.map((order) => order.Category))),
          priority: uniq(compact(stopOrders.map((order) => order.Priority))),
          isParking,
          parkingDuration,
          parkingId: uniq(compact(stopOrders.map((order) => order.ParkingId))),
          chargeKwh,
          batteryCapacity,
          consumption: stop.consumption ?? null,
        },
      });

      stop.activities.forEach((activity, activityIndex) => {
        const activityOrder = getOrdersByActivity(stopOrders, activity);
        const findOrder = activityOrder?.find((order) => order?.InternalID === activity?.jobId);
        const label = getLabelsFromOrders(activityOrder);
        const activityLocation =
          !activity.location ||
          activity.type === 'drivingRestTime' ||
          activity.type === 'workingRestTime' ||
          activity.type === 'dutyWorkingRestTime' ||
          activity.type === 'dutyDrivingRestTime'
            ? stopLocation
            : activity.location;
        const singleActivity = stop.activities.length === 1;
        const arr_time = singleActivity
          ? stop.time.arrival
          : activity.time
          ? activity.time.start
          : stop.time.arrival;

        const dep_time = singleActivity
          ? stop.time.departure
          : activity.time
          ? activity.time.end
          : stop.time.departure;

        const isPudoActivity = activity.type === 'pudo';
        const skills = findOrder?.Skills ? [findOrder.Skills] : [];
        const priority = findOrder?.Priority ? [findOrder.Priority] : [];
        const pos = findOrder?.Position
          ? [findOrder.Position]
          : isPudoActivity
          ? getPudoPositions([activity], groupsAndPudos)
          : [];
        const load = findOrder?.Demand || null;

        const locationKey = `${activityLocation.lng.toFixed(6)},${activityLocation.lat.toFixed(6)}`;
        const isFirstVisit = !visitedLocations[locationKey];
        if (isFirstVisit) {
          visitedLocations[locationKey] = true;
        }

        features.push({
          type: 'Feature',
          geometry: {
            coordinates: [activityLocation.lng, activityLocation.lat],
            type: 'Point',
          },
          properties: {
            stopLocation,
            routeId: tourIndex,
            arr_time,
            dep_time,
            types: [activity.type],
            jobIds: [activity.jobId],
            jobTags: [activity.jobTag],
            distance: stop.activities.some((a) => isRecharge(a.type)) && {
              distance: stop.distance,
              tourIndex,
            },
            tourIndex,
            tw: stop.activities
              .map((a) => getSafeValue(tw, a.jobId))
              .filter((v) => v !== undefined),
            houseKeyId: stop.activities
              .map((a) => getSafeValue(hk, a.jobId))
              .filter((v) => v !== undefined),
            maxTime: stop.activities
              .map((a) => getSafeValue(maxTime, a.jobId))
              .filter((v) => v !== undefined),
            vehicle: { type: tour.vehicleId },
            stopIndex: index,
            territory: isOrderInTerritory([activityOrder], territories),
            stopTerritory: isOrderInTerritory(stopOrders, territories),
            group: isPudoActivity
              ? getPudoGroupIds([activity], groupsAndPudos)[0] || null
              : isOrderInTerritory(activityOrder, groups),
            jobMarkerColor,
            labels: label,
            skills,
            priority,
            position: pos,
            load,
            categories: uniq(compact(stopOrders.map((order) => order.Category))),
            isParking,
            parkingDuration,
            parkingId: findOrder?.ParkingId ? [findOrder.ParkingId] : [],
            activityOrder: activityIndex + 1,
            activityIndex,
            isFirstVisit,
            totalActivitiesAtStop: stop.activities.length,
            chargeKwh,
            batteryCapacity,
            consumption: stop.consumption ?? null,
          },
        });
      });
    });

    allCoordinates.push(...coordinates);

    const line = {
      geometry: {
        coordinates,
        type: 'LineString',
      },
      type: 'Feature',
      properties: {
        routeId: tourIndex,
        cost: tour.statistic.cost,
        distance: tour.statistic.distance,
        duration: tour.statistic.duration,
        times: tour.statistic.times,
        vehicle: { type: tour.typeId, id: tour.vehicleId },
      },
    };
    line.bbox = calculateBBox(coordinates);
    features.push(line);
  });

  const unassignedCoordinates = [];

  if (depot) {
    unassignedCoordinates.push([depot.lng, depot.lat]);
    allCoordinates.push([depot.lng, depot.lat]);

    features.push({
      type: 'Feature',
      geometry: {
        coordinates: [depot.lng, depot.lat],
        type: 'Point',
      },
      properties: {
        isUnassigned: true,
        isDepot: true,
      },
    });
  }

  (solution.unassigned || []).forEach((unassigned) => {
    let coordinates = [];

    const matchedPudo = groupsAndPudos
      ?.flatMap((group) => group?.pudos || [])
      .find((pudo) => pudo?.id === unassigned?.jobId);

    if (matchedPudo) {
      matchedPudo.places.forEach((place) => {
        coordinates = [place.location.lng, place.location.lat];
        const feature = {
          type: 'Feature',
          geometry: { coordinates, type: 'Point' },
          properties: {
            jobId: place?.tag,
            reason: unassigned.reasons,
            labels: [place?.tag],
            address: place?.tag,
            types: [place?.tag],
          },
        };

        features.push(feature);
        allCoordinates.push(coordinates);
        unassignedCoordinates.push(coordinates);
      });
      return;
    }

    const job = plannedJobs.find((item) => item.id === unassigned.jobId);
    if (!job) return;

    if (job.tasks.deliveries && job.tasks.deliveries[0]) {
      job.tasks.deliveries.forEach((delivery) => {
        const places = delivery.places;
        places.forEach((place) => {
          coordinates = [place.location.lng, place.location.lat];
          const feature = getFeaturePerPlace(
            job,
            place,
            jobNumbers,
            orders,
            coordinates,
            unassigned,
            ['delivery'],
          );
          features.push(feature);
          allCoordinates.push(coordinates);
          unassignedCoordinates.push(coordinates);
        });
      });
    }
    if (job.tasks.pickups && job.tasks.pickups[0]) {
      job.tasks.pickups.forEach((pickup) => {
        const places = pickup.places;
        places.forEach((place) => {
          coordinates = [place.location.lng, place.location.lat];
          const feature = getFeaturePerPlace(
            job,
            place,
            jobNumbers,
            orders,
            coordinates,
            unassigned,
            ['pickup'],
          );
          features.push(feature);
          allCoordinates.push(coordinates);
        });
      });
    }
  });

  const valid = allCoordinates.filter((c) => c[0] && c[1]);
  if (valid.length === 0) return null;

  const bbox = calculateBBox(allCoordinates);
  const validUnassigned = unassignedCoordinates.filter((c) => c[0] && c[1]);
  const unassignedBbox = size(validUnassigned) > 0 ? calculateBBox(validUnassigned) : bbox;

  geoJSON.geo = { features, bbox, unassignedBbox };

  return geoJSON;
};

export function getGroupIdsById(data, targetId) {
  const item = data.find((entry) => entry.id === targetId);
  if (!item) return [];

  const tasks = item.tasks;
  const taskTypes = ['pickups', 'deliveries'];

  return taskTypes.flatMap((type) =>
    (tasks[type] || []).flatMap((task) => (task.places || []).map((place) => place.groupId)),
  );
}

export const convertProblemToGeoJSON = (
  { plan, fleet } = {},
  depot,
  returnLocation,
  orders = [],
  depotAddress,
  returnLocationAddress,
  editedOrder,
  territories,
  tourPlannerReloads = [],
  tourPlannerParkings = [],
  tourPlanner,
) => {
  if (!plan || !fleet) return null;

  const { jobs, groups } = plan;
  const { types } = fleet;

  if (!size(types)) return null;

  const locationsDelivery = concat(
    flatten(
      jobs.map(({ id, tasks, skills }, index) =>
        flatten(
          tasks?.deliveries
            ?.filter((task) => !isEmpty(task))
            .map((task) =>
              task.places
                .filter(({ location }) => location?.lat != null && location?.lng != null)
                .map(({ location, times }) => ({
                  coordinate: [location.lng, location.lat],
                  properties: {
                    id,
                    address: getAddressFromOrders(getOrdersByInternalID(orders, id)),
                    labels: getLabelsFromOrders(getOrdersByInternalID(orders, id)),
                    isPickup: false,
                    types: ['delivery'],
                    tw: times || [],
                    highPriority: hasHighPriorityOrders(getOrdersByInternalID(orders, id)),
                    territory: isOrderInTerritory(orders, territories),
                    group: getGroupIdsById(jobs, id),
                    skills,
                    priority: getOrdersByInternalID(orders, id)[0]?.Priority,
                    index,
                    parkingId: getOrdersByInternalID(orders, id)[0]?.ParkingId || null,
                    availableParkings: tourPlannerParkings,
                  },
                })),
            ),
        ),
      ),
    ),
    [],
  );

  const locationsPickup = concat(
    flatten(
      jobs.map(({ id, tasks, skills }, index) =>
        flatten(
          tasks?.pickups
            ?.filter((task) => !isEmpty(task))
            .map((task) =>
              task.places
                .filter(({ location }) => location?.lat != null && location?.lng != null)
                .map(({ location, times }) => ({
                  coordinate: [location.lng, location.lat],
                  properties: {
                    id,
                    address: getAddressFromOrders(getOrdersByInternalID(orders, id)),
                    labels: getLabelsFromOrders(getOrdersByInternalID(orders, id)),
                    isPickup: true,
                    types: ['pickup'],
                    tw: times || [],
                    highPriority: hasHighPriorityOrders(getOrdersByInternalID(orders, id)),
                    territory: isOrderInTerritory(orders, territories),
                    group: getGroupIdsById(jobs, id),
                    skills,
                    priority: getOrdersByInternalID(orders, id)[0]?.Priority,
                    index,
                    parkingId: getOrdersByInternalID(orders, id)[0]?.ParkingId || null,
                    availableParkings: tourPlannerParkings,
                  },
                })),
            ),
        ),
      ),
    ),
    [],
  );
  const locations = concat(locationsDelivery, locationsPickup);

  if (groups)
    locations.push(
      ...groups
        .filter((group) => Array.isArray(group.pudos) && group.pudos.length > 0)
        .flatMap(({ pudos, id: groupId }, index) =>
          pudos.flatMap(({ id, places, assignAt }) =>
            places.map(({ location, duration }) => ({
              coordinate: [location.lng, location.lat],
              properties: {
                id,
                groupId,
                group: [groupId],
                address: getAddressFromOrders(getOrdersByInternalID(orders, id)),
                labels: getLabelsFromOrders(getOrdersByInternalID(orders, id)),
                types: [ORDER_ACTIVITIES.PUDO],
                isPudo: true,
                highPriority: hasHighPriorityOrders(getOrdersByInternalID(orders, id)),
                territory: isOrderInTerritory(orders, territories),
                index,
                jobMarkerColor: tourColors[0],
                pudoDuration: duration,
                pudoAssignAt: assignAt,
              },
            })),
          ),
        ),
    );

  if (editedOrder && editedOrder.Latitude && editedOrder.Longitude) {
    locations.push({
      coordinate: [editedOrder.Longitude, editedOrder.Latitude],
      properties: {
        isEditing: true,
        types: [editedOrder.Activity],
        address: editedOrder.Address,
        labels: getLabelsFromOrders([editedOrder]),
      },
    });
  }

  const findReloadAddress = (lat, lng) => {
    const match = (tourPlannerReloads || []).find((r) =>
      compareLocationLatAndLng(r.location, { lat, lng }, 6),
    );
    return match?.address || '';
  };

  const findParkingAddress = (lat, lng) => {
    const match = tourPlannerParkings.find((p) =>
      compareLocationLatAndLng({ lat: p.lat, lng: p.lng }, { lat, lng }, 6),
    );
    return match?.address || '';
  };

  const findBreakAddress = (lat, lng) => {
    const match = (tourPlanner?.breaks || []).find((b) =>
      compareLocationLatAndLng(b.location, { lat, lng }, 6),
    );
    return match?.address || '';
  };

  const fleetStopLocations = [];
  if (Array.isArray(types)) {
    types.forEach((vehicleType) => {
      (vehicleType.shifts || []).forEach((shift) => {
        [
          ['reloads', 'reload'],
          ['breaks', 'break'],
        ].forEach(([plural, singular], typeIdx) => {
          (shift[plural] || []).forEach((stop, idx) => {
            if (stop.location?.lat && stop.location?.lng) {
              fleetStopLocations.push({
                coordinate: [stop.location.lng, stop.location.lat],
                properties: {
                  id: `fleet-${singular}-${typeIdx}-${idx}`,
                  types: [singular],
                  address:
                    singular === 'reload'
                      ? findReloadAddress(stop.location.lat, stop.location.lng)
                      : findBreakAddress(stop.location.lat, stop.location.lng),
                  ...(singular === 'reload' &&
                    stop.duration != null && { duration: stop.duration }),
                  labels: [],
                  isFleetStop: true,
                  jobMarkerColor: tourColors[0],
                },
              });
            }
          });
        });
      });
    });
  }

  if (Array.isArray(plan?.shared?.parking)) {
    plan.shared.parking.forEach((parkingEntry, idx) => {
      (parkingEntry.places || []).forEach((place, placeIdx) => {
        if (place.location?.lat && place.location?.lng) {
          fleetStopLocations.push({
            coordinate: [place.location.lng, place.location.lat],
            properties: {
              id: parkingEntry.id || `fleet-parking-${idx}-${placeIdx}`,
              types: ['parking'],
              address: findParkingAddress(place.location.lat, place.location.lng),
              parkingId: parkingEntry.id || null,
              ...(place.duration != null && { duration: place.duration }),
              labels: [],
              isFleetStop: true,
              jobMarkerColor: tourColors[0],
            },
          });
        }
      });
    });
  }

  if (tourPlanner?.breaks) {
    tourPlanner.breaks.forEach((stop, idx) => {
      if (stop.location?.lat && stop.location?.lng) {
        const alreadyAdded = fleetStopLocations.some(
          (f) => f.coordinate[0] === stop.location.lng && f.coordinate[1] === stop.location.lat,
        );
        if (!alreadyAdded) {
          fleetStopLocations.push({
            coordinate: [stop.location.lng, stop.location.lat],
            properties: {
              id: `fleet-break-tp-${idx}`,
              types: ['break'],
              address: stop.address || '',
              labels: [],
              isFleetStop: true,
              jobMarkerColor: tourColors[0],
            },
          });
        }
      }
    });
  }

  if (locations.length === 0 && fleetStopLocations.length === 0 && !depot && !returnLocation)
    return null;

  let depots = [];
  if (depot || (types && !isEmpty(types[0].shifts[0].start.location))) {
    depots = (
      depot && depot.lat && depot.lng
        ? [[depot.lng, depot.lat]]
        : flatten(
            types.map(({ shifts: [{ start: shiftStart }] }) => {
              const firstLocLat = first(locations).coordinate[1];
              const firstLocLng = first(locations).coordinate[0];

              const startLat = shiftStart?.location?.lat ?? firstLocLat;
              const startLng = shiftStart?.location?.lng ?? firstLocLng;

              const endLoc = get(types, 'shifts.end.location');
              const endLat = endLoc ? endLoc.lat : null;
              const endLng = endLoc ? endLoc.lng : null;
              if (!endLat || !endLng) return [[startLng, startLat]];
              return isEqual(startLat, endLat) && isEqual(startLng, endLng)
                ? [[startLng, startLat]]
                : [
                    [startLng, startLat],
                    [endLng, endLat],
                  ];
            }),
          )
    ).map((coordinate) => ({
      coordinate,
      properties: {
        isDepot: true,
        address: depotAddress,
      },
    }));
  }

  let returnLocationFeature = [];
  if (!isEmpty(returnLocation) && returnLocationAddress !== depotAddress) {
    returnLocationFeature = [
      {
        coordinate: [returnLocation.lng, returnLocation.lat],
        properties: {
          isReturnLocation: true,
          address: returnLocationAddress,
        },
      },
    ];
  }

  return {
    features: concat(
      mapFeatures(locations),
      mapFeatures(fleetStopLocations),
      mapFeatures(depots),
      mapFeatures(returnLocationFeature),
    ),
    bbox: calculateBBox(
      concat(
        locations.map(({ coordinate }) => coordinate),
        fleetStopLocations.map(({ coordinate }) => coordinate),
        depots.map(({ coordinate }) => coordinate),
        returnLocationFeature.map(({ coordinate }) => coordinate),
      ),
    ),
    type: 'FeatureCollection',
  };
};

export const hideDepot = ({ geo } = {}) =>
  geo
    ? {
        geo: {
          ...geo,
          features: filter(geo.features, ({ properties: { isDepot } }) => !isDepot),
        },
      }
    : {};
